import React from 'react';
import { defineMessages, FormattedHTMLMessage } from 'react-intl';

import replayIconUrl from '../../static/images/replay.png';

export const MiniCoachmarkConstants = Object.freeze({
  DEFAULT_TIMEOUT: 15000,
});

export const MiniCoachmarkFrequencyLimits = Object.freeze({
  BINGE_SKIPPER_THROTTLE_MINUTES: 60,
  EDUCATION_OVERLAY_MAX_VIEWS: 1,
  PREMIUM_ACCESS_MAX_VIEWS: 1,
  FIRST_TIME_NOW_PLAYING_SEARCH_MAX_VIEWS: 1,
  FIRST_TIME_DISCOVERY_TUNER_MAX_VIEWS: 1,
  FIRST_TIME_ARTIST_TAKEOVER_MAX_VIEWS: 7,
  FIRST_TIME_MY_STATIONS_SEARCH_MAX_VIEWS: 1,
  FIRST_TIME_PODCAST_NOW_PLAYING_MAX_VIEWS: 1,
  FIRST_TIME_PODCAST_COLLECTIONS_MAX_VIEWS: 3,
});

export const MiniCoachmarkKeys = Object.freeze({
  OPEN_MINI_COACHMARK_ID: 'OPEN_MINI_COACHMARK_ID',
  LAST_REPLAY_DATE: 'LAST_REPLAY_DATE',
  LAST_VIEWING_DATE: 'LAST_VIEWING_DATE',
  NUMBER_OF_VIEWINGS: 'NUMBER_OF_VIEWINGS',
});

// frequency limiting of these found in lib/overlay isAnchoredOverlayWithinFrequencyCap
export const MiniCoachmarkTypes = Object.freeze({
  NOW_PLAYING_REPLAY: 'NOW_PLAYING_REPLAY',
  SKIP_LIMIT_REACHED: 'SKIP_LIMIT_REACHED',
  SKIP_LIMIT_REACHED_FLEX: 'SKIP_LIMIT_REACHED_FLEX',
  THUMB_UP: 'THUMB_UP',
  THUMB_DOWN: 'THUMB_DOWN',
  MTUP_PLAYLIST: 'MTUP_PLAYLIST',
  REPLAY_NOT_AVAILABLE_LICENSING: 'REPLAY_NOT_AVAILABLE_LICENSING',
  REPLAY_NOT_AVAILABLE_LICENSING_HISTORY: 'REPLAY_NOT_AVAILABLE_LICENSING_HISTORY',
  BINGE_SKIPPER: 'BINGE_SKIPPER',
  FIRST_TIME_FEEDBACK: 'FIRST_TIME_FEEDBACK',
  FIRST_TIME_PREMIUM_WELCOME_SEARCH: 'FIRST_TIME_PREMIUM_WELCOME_SEARCH',
  FIRST_TIME_PREMIUM_ACCESS: 'FIRST_TIME_PREMIUM_ACCESS',
  FIRST_TIME_NOW_PLAYING_SEARCH: 'FIRST_TIME_NOW_PLAYING_SEARCH',
  FIRST_TIME_DISCOVERY_TUNER: 'FIRST_TIME_DISCOVERY_TUNER',
  FIRST_TIME_ARTIST_TAKEOVER: 'FIRST_TIME_ARTIST_TAKEOVER',
  FIRST_TIME_ARTIST_TAKEOVER_DEEP_LINK: 'FIRST_TIME_ARTIST_TAKEOVER_DEEP_LINK',
  FIRST_TIME_MY_STATIONS_SEARCH: 'FIRST_TIME_MY_STATIONS_SEARCH',
  FIRST_TIME_PODCAST_NOW_PLAYING: 'FIRST_TIME_PODCAST_NOW_PLAYING',
  FIRST_TIME_PODCAST_COLLECTIONS: 'FIRST_TIME_PODCAST_COLLECTIONS',
});

export const messages = defineMessages({
  replayNotAvailable: {
    id: 't.replayNotAvailable',
    defaultMessage: 'This song can’t be replayed.',
  },
  mtupPlaylist: {
    id: 't.mtupPlaylist',
    defaultMessage:
      '<b>All your thumbs in one playlist.</b> <br> As you continue to thumb songs in stations, this playlist will grow.',
  },
  replayTrack: {
    id: 't.replayTrack',
    defaultMessage: 'Click {img} to replay this track',
    values: {
      img: React.createElement('img', {
        src: replayIconUrl,
        style: {
          display: 'inline-block',
          height: 19,
          width: 16,
          textAlign: 'top',
          margin: '0 5px',
        },
      }),
    },
  },
  skipNotAvailable: {
    id: 't.skipNotAvailable',
    defaultMessage: 'Our music licenses limit the number of tracks you can skip.',
  },
  skipNotAvailableFlex: {
    id: 't.skipNotAvailableFlex',
    defaultMessage: 'This song can’t be skipped.',
  },
  thumbDown: {
    id: 't.thumbDown',
    defaultMessage:
      "Thanks for your input. When this track is over, we'll never play it again on this station.",
  },
  bingeSkipperTitle: {
    id: 't.bingeSkipperTitle',
    defaultMessage: 'Not Loving This Station?',
  },
  bingeSkipperBody: {
    id: 't.bingeSkipperBody',
    defaultMessage: 'Try another one and discover new favorites.',
  },
  bingeSkipperButton: {
    id: 't.bingeSkipperButton',
    defaultMessage: 'Browse',
  },
  firstTimeFeedbackThumbsUpTitle: {
    id: 't.firstTimeFeedbackThumbsUpTitle',
    defaultMessage: 'Thumbs Up',
  },
  firstTimeFeedbackThumbsUpText: {
    id: 't.firstTimeFeedbackThumbsUpText',
    defaultMessage: ' adds similar songs to this station.',
  },
  firstTimeFeedbackThumbsDownTitle: {
    id: 't.firstTimeFeedbackThumbsDownTitle',
    defaultMessage: 'Thumbs Down',
  },
  firstTimeFeedbackThumbsDownText: {
    id: 't.firstTimeFeedbackThumbsDownText',
    defaultMessage: ' removes a song from this station.',
  },
  firstTimePremiumWelcomeSearchText: {
    id: 't.firstTimePremiumWelcomeSearchText',
    defaultMessage: 'Search for artists, albums, songs, and stations.',
  },
  firstTimeDiscoveryTunerHeader: {
    id: 't.firstTimeDiscoveryTunerHeader',
    defaultMessage: 'Switch it up! Introducing Modes on Pandora',
  },
  firstTimeDiscoveryTunerText: {
    id: 't.firstTimeDiscoveryTunerText',
    defaultMessage: 'Modes give you the control to fine tune your station.',
  },
  firstTimeWelcomeSearchText: {
    id: 't.firstTimeWelcomeSearchText',
    values: { boldNew: <b>New!</b> },
    defaultMessage: '{boldNew} Search and play your favorite songs on-demand for free.',
  },
  firstTimePodcastNowPlayingText: {
    id: 't.firstTimePodcastNowPlayingText',
    defaultMessage: 'Tap + to collect podcasts and episodes.',
  },
  firstTimePodcastCollectionsHeader: {
    id: 't.firstTimePodcastCollectionsHeader',
    defaultMessage: 'Podcasts are available on the web! {lineBreak}',
  },
  firstTimePodcastCollectionsText: {
    id: 't.firstTimePodcastCollectionsText',
    defaultMessage:
      "Here's where you can find them. Not sure what to listen to? {lineBreak} Browse dozens of categories.",
  },
  browsePodcasts: {
    id: 't.browsePodcasts',
    defaultMessage: 'Browse Podcasts',
  },
  artistTakeOverHeader: {
    id: 't.artistTakeOverHeader',
    defaultMessage: 'New! Modes made by {artistName}',
  },
  artistTakeOverSubHeader: {
    id: 't.artistTakeOverSubHeader',
    defaultMessage: 'Modes give you the control to fine tune your station.',
  },
});

export const MTUP_COACHMARK_CONFIG = {
  coachmarkConfigs: [
    {
      id: MiniCoachmarkTypes.MTUP_PLAYLIST,
      message: <FormattedHTMLMessage {...messages.mtupPlaylist} />,
      targetOrigin: 'top',
      shouldScroll: true,
      timeout: 0,
    },
  ],
};
